import React, {
  ComponentType,
  Dispatch,
  Key,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';

import { KeyToValueOfType } from '@paragon/utilities';

export function formatHexColor(hexColor: number): string {
  return `#${hexColor.toString(16)}`;
}

/**
 * A hook to enable delayed loading in a react component.  The provided callback will be called every time the
 *   component renders as long as it returns false.
 *
 * @param callback A function that checks if the relevant data has been loaded.  If not, it should return false.
 *
 * @example
 *
 * const priceStatus = useSelector(getFetchPriceStatus);    // The request has already been created to fetch the price.
 * const [price, setPrice] = useState(number | null>(null); // Once it arrives, we want to store it in a state hook.
 * useLoadMonitor(() => {
 *   if (priceStatus.loading) {
 *     return false; // Since the data is still loading, return false.
 *   } else {
 *     setPrice(priceStatus.value);
 *     // The data is now loaded.  Since we don't return false, this monitor won't be called again.
 *   }
 * });
 */
export function useLoadMonitor(callback: () => boolean | void) {
  const [isLoaded, setIsLoaded] = useState(false);

  if (!isLoaded && callback() !== false) {
    setIsLoaded(true);
  }
}

type KeyOrNullish = Key | null | undefined;
type PropertyKeyProducer<P> = KeyToValueOfType<P, KeyOrNullish> | ((props: P) => KeyOrNullish);

// tslint:disable-next-line:max-line-length
// https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
export function withKey<P extends {}>(key: PropertyKeyProducer<P>, component: ComponentType<P>): ComponentType<P> {
  return (props: PropsWithChildren<P>) => {
    const keyValue = typeof key === 'function' ? key(props) : (props[key] as KeyOrNullish);
    return React.createElement(component, { ...props, key: keyValue }, props.children);
  };
}

/**
 * A hook which produces a function that can be called to determine if the component is still mounted.  Useful when
 *   you need to set state in response to an asynchronous action and there's a chance the component might no longer
 *   exist when that action completes.
 */
export function useIsMounted() {
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => void (mounted.current = false);
  });

  return useCallback(() => mounted.current, [mounted]);
}

export function useTimeout() {
  const timeoutIdRef = useRef<number>(undefined);
  useEffect(
    () => () => {
      window.clearTimeout(timeoutIdRef.current);
    },
    [],
  );

  return useCallback((callback: () => void, ms: number) => {
    window.clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = window.setTimeout(callback, ms);
  }, []);
}

export const DELAYED_SPINNER_DEFAULT_TIMEOUT = 300;

/**
 * Works similarly to `useState<boolean>()`, except two boolean values will be returned. The second will follow the
 *   first at the specified timeout value. Useful when you want to show a progress bar only when an operation is taking
 *   longer than expected.
 *
 *   @example
 *   const [instantValue, delayedValue, setValue] = useDelayedBoolean(false, 300);
 *
 *   setValue(true);
 *   console.log(instantValue, delayedValue);  // true, false
 *
 *   // 300 ms later, another render will trigger
 *   console.log(instantValue, delayedValue);  // true, true
 */
export function useDelayedBoolean(
  defaultValue = false,
  delay = DELAYED_SPINNER_DEFAULT_TIMEOUT,
): [boolean, boolean, Dispatch<SetStateAction<boolean>>] {
  const setTimeout = useTimeout();

  const [value, setValue] = useState(defaultValue);
  const [delayedValue, setDelayedValue] = useState(defaultValue);

  const setValueWithDelay = useCallback(
    (param: SetStateAction<boolean>) => {
      const evaluatedParam = typeof param === 'boolean' ? param : param(value);

      setValue(evaluatedParam);
      setTimeout(() => setDelayedValue(evaluatedParam), delay);
    },
    [delay, setTimeout, value],
  );

  return [value, delayedValue, setValueWithDelay];
}

export function usePrevious<T>(value: T, initialValue?: T): T | undefined {
  const ref = useRef<T>(initialValue);
  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export const timeoutAsync = (timeout?: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });

/**
 * Throws an error with the provided message, unless in a production environment.
 */
export function assertUnlessInProduction(message: string): void;

/**
 * Throws an error with the provided message if the condition is false. In a production environment, the message will
 *   only be logged as a warning instead.
 */
export function assertUnlessInProduction(condition: boolean, message: string): void;

export function assertUnlessInProduction(conditionOrMessage: boolean | string, message?: string): void {
  if (conditionOrMessage === true) return;
  message = conditionOrMessage || message;

  if (process.env.PRODUCTION) {
    console.error(message);
    Sentry.captureMessage(`Assertion failed in assertUnlessInProduction: ${message}`);
  } else {
    throw new Error(message);
  }
}

// FIXME: rewrite to use `isSubsetOf` or `union` once those are supported enough
export function areSetsEqual<T>(a: Set<T>, b: Set<T>) {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const item of a) if (!b.has(item)) return false;
  return true;
}

export function upsertLocally<T extends { guid: string }>(
  existingObjects: Record<string, T>,
  addedObjects: T[],
): Record<string, T> {
  existingObjects = Object.assign({}, existingObjects);

  for (const envelope of addedObjects) {
    existingObjects[envelope.guid] = envelope;
  }

  return existingObjects;
}

/**
 * Allows an operation to be scheduled after a delay or ran immediately. Scheduling a new operation cancels a previously
 *   scheduled operation, or call `cancel()` to cancel one manually.
 */
export function useDelayedOperation(callback: () => void, defaultDelay: number) {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<number | null>(null);

  callbackRef.current = callback;

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const schedule = useCallback(
    (options?: { delay?: number }) => {
      cancel();
      timeoutRef.current = window.setTimeout(callbackRef.current, options?.delay ?? defaultDelay);
    },
    [cancel, defaultDelay],
  );

  const runImmediately = useCallback(() => {
    cancel();
    callbackRef.current();
  }, [cancel]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  return useMemo(() => ({ schedule, runImmediately, cancel }), [cancel, runImmediately, schedule]);
}

interface PromisePending {
  readonly isPending: true;
  readonly isSuccess: false;
  readonly isError: false;
  readonly data: undefined;
  readonly error: undefined;
}

interface PromiseFulfilled<T> {
  readonly isPending: false;
  readonly isSuccess: true;
  readonly isError: false;
  readonly data: T;
  readonly error: undefined;
}

interface PromiseRejected {
  readonly isPending: false;
  readonly isSuccess: false;
  readonly isError: true;
  readonly data: undefined;
  readonly error: unknown;
}

type PromiseState<T> = PromisePending | PromiseFulfilled<T> | PromiseRejected;

export function usePromise<T>(createPromise: () => Promise<T>): PromiseState<T> {
  const [promiseState, setPromiseState] = useState<PromiseState<T>>({
    isPending: true,
    isSuccess: false,
    isError: false,
    data: undefined,
    error: undefined,
  });

  useEffect(() => {
    let ignore = false;

    async function startPromise() {
      try {
        const data = await createPromise();
        if (!ignore) {
          setPromiseState({ isPending: false, isSuccess: true, isError: false, data, error: undefined });
        }
      } catch (error) {
        if (!ignore) {
          setPromiseState({ isPending: false, isSuccess: false, isError: true, data: undefined, error });
        }
      }
    }

    startPromise();

    return () => {
      ignore = true;
    };
  }, [createPromise]);

  return promiseState;
}
